import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { 
  Box, Card, Tabs, Tab, Container, Button, Stack, 
  Divider, Paper, Avatar, CircularProgress, Typography, Fab, Tooltip,
  IconButton
} from "@mui/material";
import { 
  MedicalInformation, HistoryEdu, PlaylistAddCheck, 
  SettingsSuggest, FactCheck, NotificationImportant,
  Save as SaveIcon, Close as CloseIcon
} from "@mui/icons-material";

import { getMedicalConditions, updateMedicalConditions } from "../../../../api/services/Client/medicalConditions";

import ConditionsChecklist from "./sections/ConditionsChecklist";
import MedicalHistory from "./sections/MedicalHistory";
import FlashMessages from "./sections/FlashMessages";
import ConditionMapping from "./sections/ConditionMapping";
import VitalParameters from "./sections/VitalParameters";
import ConfirmModal from "../../../ConfirmModal/ConfirmModal";
import { QK } from "../../../../base/qk";
import useClientDetails from "../../../../hooks/useClientDetails";

const MedicalConditionsAndAlerts = () => {
  const { ClientID: clientId } = useParams();
  const { fullName } = useClientDetails(clientId);
  const { hash } = useLocation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  
  const [openConfirm, setOpenConfirm] = useState(false);
  const activeTab = hash.replace("#", "") || "conditions";
  
  const themeColors = { 
    primary: "#0f172a",
    accent: "#6366f1", 
    bg: "#f8fafc",
    border: "#f1f5f9" 
  };

  const [formData, setFormData] = useState({
    checklist: [],
    medical_history: "",
    flash_message: "",
    display_until: "",
    mapping_data: {},
    vital_targets: [],
    weight_change_alert_kg: "",
    fluid_target_required: false,
    fluid_target_ml: ""
  });

  const { isLoading } = useQuery(
    [QK.medicalConditions, clientId],
    () => getMedicalConditions(clientId),
    {
      enabled: !!clientId,
      onSuccess: (data) => data && setFormData(data),
    }
  );

  const { mutate: saveRecord, isLoading: isSaving } = useMutation(
    (payload) => updateMedicalConditions(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["medicalConditions", clientId]);
        enqueueSnackbar("Medical record updated successfully", { variant: "success" });
        setOpenConfirm(false);
      },
      onError: (err) => {
        enqueueSnackbar(err.response?.data?.message || "Failed to update", { variant: "error" });
        setOpenConfirm(false);
      }
    }
  );

  const handleConfirmSave = () => {
    saveRecord({ 
      client_id: clientId, 
      data: {
        ...formData,
        fluid_target_ml: formData.fluid_target_ml ? Number(formData.fluid_target_ml) : 0,
        display_until: formData.display_until || null
      } 
    });
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress thickness={5} size={50} sx={{ color: themeColors.accent }} />
    </Box>
  );

  return (
    <Box sx={{ width: "100%", bgcolor: themeColors.bg, minHeight: "100vh", pb: 12 }}>
      <ConfirmModal
        show={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleConfirm={handleConfirmSave}
        isLoading={isSaving}
        title="Update Clinical Data"
        body="Are you sure you want to commit these changes to the patient's permanent medical record?"
        confirmText="Confirm Update"
      />

      <Box sx={{ bgcolor: "white", borderBottom: `1px solid ${themeColors.border}`, position: 'sticky', top: 0, zIndex: 10 }}>
        <Container maxWidth="xl">
          <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 2 }}>
            <Avatar sx={{ 
              bgcolor: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)", 
              width: 48, height: 48, borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)"
            }}>
              <MedicalInformation sx={{ color: "white", fontSize: 26 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: themeColors.primary, lineHeight: 1.2 }}>
                Medication Conditions & Alerts
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Name: {fullName}
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Card sx={{ 
          borderRadius: "24px", 
          boxShadow: "0 1px 3px rgba(0,0,0,0.02), 0 20px 25px -5px rgba(0,0,0,0.03)",
          border: `1px solid ${themeColors.border}`,
          bgcolor: "white"
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, v) => window.location.hash = v} 
            variant="scrollable"
            sx={{ 
              px: 3, pt: 1, borderBottom: `1px solid ${themeColors.border}`,
              "& .MuiTab-root": { 
                py: 0, px: 4, fontWeight: 700, fontSize: "0.85rem", textTransform: "none", color: "#64748b",
                transition: 'all 0.2s'
              },
              "& .Mui-selected": { color: themeColors.accent },
              "& .MuiTabs-indicator": { height: 4, borderRadius: "4px 4px 0 0", bgcolor: themeColors.accent }
            }}
          >
            <Tab icon={<PlaylistAddCheck sx={{ fontSize: 20 }} />} iconPosition="start" label="Health Checklist" value="conditions" />
            <Tab icon={<HistoryEdu sx={{ fontSize: 20 }} />} iconPosition="start" label="Medical History" value="history" />
            <Tab icon={<NotificationImportant sx={{ fontSize: 20 }} />} iconPosition="start" label="Flash Alerts" value="flash" />
            <Tab icon={<FactCheck sx={{ fontSize: 20 }} />} iconPosition="start" label="Condition Mapping" value="mapping" />
            <Tab icon={<SettingsSuggest sx={{ fontSize: 20 }} />} iconPosition="start" label="Vital Parameters" value="vitals" />
          </Tabs>

          <Box sx={{ p: { xs: 3, md: 6 } }}>
            {activeTab === "conditions" && <ConditionsChecklist themeColors={themeColors} value={formData.checklist} onChange={(v) => setFormData({...formData, checklist: v})} />}
            {activeTab === "history" && <MedicalHistory value={formData.medical_history} onChange={(v) => setFormData({...formData, medical_history: v})} />}
            {activeTab === "flash" && <FlashMessages formData={formData} setFormData={setFormData} />}
            {activeTab === "mapping" && <ConditionMapping value={formData.mapping_data} onChange={(v) => setFormData({...formData, mapping_data: v})} />}
            {activeTab === "vitals" && <VitalParameters formData={formData} setFormData={setFormData} />}
          </Box>
        </Card>
      </Container>

<Box sx={{ 
        position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, width: 'auto', minWidth: '350px'
      }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1.2, 
            borderRadius: "100px", 
            display: "flex", 
            gap: 2, 
            alignItems: "center",
            bgcolor: "rgba(255, 255, 255, 0.85)", // Light frosted white
            backdropFilter: "blur(16px) saturate(180%)", // Stronger glass effect
            border: "1px solid rgba(226, 232, 240, 0.8)", // Subtle slate border
            boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Tooltip title="Discard Changes">
            <IconButton 
              sx={{ 
                color: "#94a3b8", 
                ml: 1, 
                bgcolor: "#f8fafc", // Very light grey circle
                "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } 
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0", my: 1 }} />
          
          <Box sx={{ px: 1, display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="caption" sx={{ color: "#64748b", display: 'block', lineHeight: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Status
            </Typography>
            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 700 }}>
              Unsaved Changes
            </Typography>
          </Box>

          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={() => setOpenConfirm(true)}
            sx={{ 
              bgcolor: themeColors.accent, 
              color: "white", 
              px: 4, 
              py: 1.2,
              borderRadius: "100px", 
              textTransform: "none", 
              fontWeight: 700,
              fontSize: "0.85rem", 
              boxShadow: `0 8px 20px -6px ${themeColors.accent}66`, // Soft glow shadow
              "&:hover": { 
                bgcolor: "#4f46e5", 
                transform: "translateY(-2px)",
                boxShadow: `0 12px 24px -6px ${themeColors.accent}88`,
              },
              "&:active": { transform: "translateY(0)" }
            }}
          >
            Save Record
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default MedicalConditionsAndAlerts;